import {
  Avatar,
  Button,
  Checkbox,
  Container,
  Divider,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ConsEquipOrderIdentifierType,
  IEquipmentItem,
  getEquipmentByID,
} from 'API';
import { ControlledSelectInput } from 'Components/ControlledSelectInput';
import { EquipmentContext } from 'Context';
import { format } from 'date-fns';
import { add } from 'date-fns/esm';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useIsDesktop } from 'Hooks';
import {
  BigButton,
  LeftArrowWhite,
  PurpleChecked,
  PurpleUnchecked,
} from 'Images';
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { illegalChars, lettersOrNumbersWithSomeExtraCharsMessage } from 'Regex';
import { getPeriodLabel, getPeriodNumber } from 'Utils/periods';
import { ExistingOrderDialog } from '../ExistingOrderDialog';
import { RowProduct } from '../RowProduct';
import { SuccessOrderDialog } from '../SuccessOrderDialog';
import { SummaryOrderDialog } from '../SummaryOrderDialog';
import { RowProductDisabled } from '../RowProductDisabled';

interface IEquipmentOrderItem extends IEquipmentItem {
  checked: boolean;
  overQuantity?: boolean;
}
export interface IOrderSummaryData {
  note: string;
  startDate: Date;
  endDate: Date;
  orderItems: IEquipmentItem[];
}

export const CreateOrderForm = () => {
  const { availableItems, recentOrders } = useContext(EquipmentContext);
  const MAX_LENGTH = 100;
  const [newEquipmentItems, setNewEquipmentItems] = useState<
    IEquipmentOrderItem[]
  >([]);
  const [oldEquipmentItems, setOldEquipmentItems] = useState<
    IEquipmentOrderItem[]
  >([]);
  const [checkAll, setCheckAll] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [period, setPeriod] = useState(1);
  const [originalOrderNote, setOriginalOrderNote] = useState('');
  const [orderSummaryData, setOrderSummaryData] = useState<IOrderSummaryData>({
    note: '',
    startDate: new Date(),
    endDate: add(new Date(), { months: period }),
    orderItems: [],
  });
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isExistingOrderDialogOpen, setIsExistingOrderDialogOpen] =
    useState(false);
  const [error, setError] = useState(false);
  const [errorSelectItem, setErrorSelectItem] = useState(false);
  const urlParams = useParams();
  const [showOldItems, setShowOldItems] = useState(Boolean(urlParams?.id));
  const [scrolled, setScrolled] = useState(false);
  const [orderNoteCount, setOrderNoteCount] = useState(0);
  const navigate = useNavigate();

  const isDesktop = useIsDesktop();
  const dictionary = Dictionary[DICTIONARY.Equipment].createOrder;

  const lastYearDate = new Date();
  lastYearDate.setFullYear(lastYearDate.getFullYear() - 1);
  const firstOldItemIndex =
    availableItems &&
    availableItems.items.findIndex((item) => {
      if (!item.lastOrderOfThatItem) return false;
      return new Date(item.lastOrderOfThatItem) <= lastYearDate;
    });

  useEffect(() => {
    if (!isDesktop) {
      const handleScroll = () => {
        if (window.scrollY > 550) {
          setScrolled(true);
        } else {
          setScrolled(false);
        }
      };
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isDesktop]);

  const getFirstOldItemIndex = () => {
    return firstOldItemIndex === -1
      ? availableItems.items.length
      : firstOldItemIndex;
  };

  const initializeEquipmentItems = useCallback(() => {
    if (availableItems.items.length === 0) navigate('/equipment');
    window.scrollTo(0, 0);
    setNewEquipmentItems(
      availableItems.items?.slice(0, getFirstOldItemIndex()).map((item) => ({
        ...item,
        quantity: item.maxAVGQuantityPerMonth || 1,
        itemNote: item.itemNote,
        checked: false,
      }))
    );
    setOldEquipmentItems(
      availableItems.items?.slice(getFirstOldItemIndex()).map((item) => ({
        ...item,
        quantity: item.maxAVGQuantityPerMonth || 1,
        itemNote: item.itemNote,
        checked: false,
      }))
    );

    // BUG 7252 - open oldEquipmentItems when only have oldEquipmentItems
    setShowOldItems(
      newEquipmentItems.length < 1 && oldEquipmentItems.length > 0
    );

    if (recentOrders.length > 0) {
      // Open existing order dialog if last order date is less then 3 days ago.
      if (add(new Date(recentOrders[0].createDate), { days: 3 }) > new Date()) {
        setIsExistingOrderDialogOpen(true);
      }
    }
  }, [availableItems.items, recentOrders]);

  const initDuplicateEquipmentOrder = useCallback(async () => {
    if (!urlParams.id) return;

    // Make duplicate order
    const equipType =
      urlParams.id && urlParams.id?.length < 12
        ? ConsEquipOrderIdentifierType.IntReqNumber
        : ConsEquipOrderIdentifierType.ShemeshRequestNumber;
    const equipmentOrderInfo = await getEquipmentByID(urlParams.id, equipType);
    if (equipmentOrderInfo) {
      setOriginalOrderNote(equipmentOrderInfo?.note || '');
      checkOrderItemsHandler(equipmentOrderInfo?.orderItems);
      setCheckAll(true);
    }
  }, [urlParams.id]);

  useEffect(() => {
    initDuplicateEquipmentOrder();
  }, [urlParams.id]);

  useEffect(
    () => initializeEquipmentItems(),
    [availableItems, initializeEquipmentItems, recentOrders]
  );

  const handleChangedPeriod = (value: string) => {
    setPeriod(getPeriodNumber(value));

    setNewEquipmentItems(
      newEquipmentItems.map((item) => ({
        ...item,
        quantity: item.maxAVGQuantityPerMonth * getPeriodNumber(value) || 1,
      }))
    );
    setOldEquipmentItems(
      oldEquipmentItems.map((item) => ({
        ...item,
        quantity: item.maxAVGQuantityPerMonth * getPeriodNumber(value) || 1,
      }))
    );
  };

  const handleToggle =
    (index: number, isOld: boolean) => (e: React.MouseEvent) => {
      e.stopPropagation();

      const newItems = isOld ? [...oldEquipmentItems] : [...newEquipmentItems];
      newItems[index].checked = !newItems[index].checked;
      isOld ? setOldEquipmentItems(newItems) : setNewEquipmentItems(newItems);
      if (newItems.filter((item) => item.checked).length > 0)
        setErrorSelectItem(false);
    };
  const handleCheckItem =
    (index: number, isOld: boolean) => (checked: boolean) => {
      const newItems = isOld ? [...oldEquipmentItems] : [...newEquipmentItems];

      newItems[index].checked = newItems[index].checked
        ? newItems[index].checked
        : checked;
      isOld ? setOldEquipmentItems(newItems) : setNewEquipmentItems(newItems);
    };

  const onSubmitHandler = (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newItems: IEquipmentOrderItem[] = [
      ...(newEquipmentItems ? newEquipmentItems : []),
      ...(showOldItems ? oldEquipmentItems : []),
    ]
      .map((item, i) => ({ item, i }))
      .filter((item) => item.item.checked)
      .map((item) => ({
        ...item.item,
        quantity: e.target[`item-quantity-field-${item.i}`]?.value,
        itemNote: e.target[`item-note-field-${item.i}`]?.value,
        quantityNote: e.target[`item-quantity-note-field-${item.i}`]?.value,
        overQuantity:
          e.target[`item-quantity-field-${item.i}`]?.value &&
          e.target[`item-quantity-field-${item.i}`]?.value >
            item.item.maxAVGQuantityPerMonth * period
            ? true
            : false,
      }));

    const orderItems = [...newItems];

    if (orderItems.length > 0) {
      setErrorSelectItem(false);
      setOrderSummaryData({
        note: orderItems.some((item) => item.overQuantity)
          ? `${e.target['order-note']?.value} --- ${dictionary.noteInfoError}`
          : e.target['order-note']?.value,
        startDate: new Date(),
        endDate: add(new Date(), { months: period }),
        orderItems,
      });
      setIsDialogOpen(true);
    } else {
      setErrorSelectItem(true);
    }
  };

  const checkAllCheckboxHandler = () => {
    const newItems = newEquipmentItems.map((item) => ({
      ...item,
      ...(item.isActive && { checked: !checkAll }),
    }));
    setNewEquipmentItems(newItems);
    if (showOldItems) {
      const oldItems = oldEquipmentItems.map((item) => ({
        ...item,
        ...(item.isActive && { checked: !checkAll }),
      }));
      setOldEquipmentItems(oldItems);
    }
    setCheckAll(!checkAll);
  };

  const checkOrderItemsHandler = (originalOrderItems: any) => {
    const newItems = availableItems.items
      .slice(0, getFirstOldItemIndex())
      .map((item) => ({
        ...item,
        quantity: item.maxAVGQuantityPerMonth || 1,
        itemNote: item.itemNote,
        checked: false,
      }));
    for (let orgItem of originalOrderItems) {
      for (let item of newItems) {
        if (orgItem.itemCatalogNumber === item.itemCatalogNumber) {
          if (item.isActive) {
            item.checked = true;
          }
          // item.quantity = orgItem.quantity;
          item.itemNote = orgItem.itemNote;
          item.quantityNote = orgItem.quantityNote;
        }
      }
    }
    setNewEquipmentItems(newItems);
    if (showOldItems) {
      const oldItems = availableItems.items
        .slice(getFirstOldItemIndex())
        .map((item) => ({
          ...item,
          quantity: item.maxAVGQuantityPerMonth || 1,
          itemNote: item.itemNote,
          checked: false,
        }));
      for (let orgItem of originalOrderItems) {
        for (let item of oldItems) {
          if (orgItem.itemCatalogNumber === item.itemCatalogNumber) {
            if (item.isActive) {
              item.checked = true;
            }
            // item.quantity = orgItem.quantity;
            item.itemNote = orgItem.itemNote;
            item.quantityNote = orgItem.quantityNote;
          }
        }
      }
      setOldEquipmentItems(oldItems);
    }
  };

  const getItemsToDisplay = () => {
    return [...newEquipmentItems, ...(showOldItems ? oldEquipmentItems : [])];
  };

  return (
    <form
      onSubmit={onSubmitHandler}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <Typography component='h5' mb={1} tabIndex={0}>
        {dictionary.periodOrderTitle}
      </Typography>
      <Typography variant='body3' mb={3} tabIndex={0}>
        {dictionary.periodOrderLabel}
      </Typography>
      <ControlledSelectInput
        label={dictionary.periodOrder}
        options={[
          dictionary.month1,
          dictionary.month2,
          dictionary.month3,
          dictionary.month6,
        ]}
        setValue={handleChangedPeriod}
        value={getPeriodLabel(period)}
        sx={{ width: '400px', '& label': { direction: 'rtl' } }}
      />

      <Stack
        direction='row'
        alignItems='center'
        p='4px 4px'
        onClick={checkAllCheckboxHandler}
        sx={{ cursor: 'pointer', mb: isDesktop ? 3 : 2, mt: 5 }}
      >
        <ListItemIcon
          sx={{
            minWidth: isDesktop ? '20px' : 'unset',
          }}
        >
          <Checkbox
            edge='start'
            checked={checkAll}
            inputProps={{ id: 'check-all' }}
            icon={
              <Avatar
                alt=''
                src={PurpleUnchecked}
                sx={{ width: '24px', height: '24px' }}
              />
            }
            checkedIcon={
              <Avatar
                alt=''
                src={PurpleChecked}
                sx={{ width: '24px', height: '24px' }}
              />
            }
          />
        </ListItemIcon>
        <ListItemText
          primary={
            <label
              style={{
                pointerEvents: 'none',
                fontSize: isDesktop ? '24px' : '18px',
                fontWeight: 'bold',
              }}
              htmlFor='check-all'
            >
              {dictionary.selectAllItems}{' '}
              {getItemsToDisplay().filter((item) => item.checked).length === 0
                ? null
                : `(${
                    getItemsToDisplay().filter((item) => item.checked).length
                  })`}
            </label>
          }
          sx={{
            ml: isDesktop ? undefined : '9px',
            color: isDesktop ? '#3A556C' : undefined,
          }}
        />
      </Stack>
      <ul style={{ padding: 0, margin: 0 }}>
        {newEquipmentItems.length > 0 &&
          newEquipmentItems
            ?.slice(0, getFirstOldItemIndex())
            .map((item: IEquipmentOrderItem, index) =>
              item.isActive ? (
                <RowProduct
                  key={index}
                  item={item}
                  handleCheckItem={handleCheckItem(index, false)}
                  handleToggle={handleToggle(index, false)}
                  index={index}
                  period={period}
                  checked={item.checked}
                />
              ) : (
                <RowProductDisabled key={index} item={item} index={index} />
              )
            )}
      </ul>
      {availableItems.items.length > 0 && (
        <>
          {newEquipmentItems.length > 0 && oldEquipmentItems.length > 0 && (
            <Container sx={{ width: '100%', m: '80px 0 40px' }}>
              <Stack
                flexDirection='row'
                alignItems='center'
                justifyContent='center'
                gap='8px'
                sx={{
                  cursor: 'pointer',
                }}
                width='fit-content'
                m='0 auto'
                onClick={() => setShowOldItems(!showOldItems)}
                tabIndex={0}
                role='button'
                aria-label={dictionary.oldItems}
                aria-expanded={showOldItems}
              >
                <Typography
                  component='h3'
                  variant='body5'
                  color='primary'
                  mb={1}
                >
                  {dictionary.oldItems}
                </Typography>
                <Avatar
                  alt=''
                  src={BigButton}
                  sx={{
                    mb: 1,
                    width: '37px',
                    height: '37px',
                    transform: showOldItems
                      ? 'rotate(90deg)'
                      : 'rotate(270deg)',
                  }}
                />
              </Stack>
            </Container>
          )}
          {getFirstOldItemIndex() >= 0 && (
            <ul style={{ padding: 0, margin: 0 }}>
              {showOldItems &&
                oldEquipmentItems.length > 0 &&
                oldEquipmentItems.map((item: IEquipmentOrderItem, index) =>
                  item.isActive ? (
                    <RowProduct
                      key={index + newEquipmentItems.length}
                      item={item}
                      handleCheckItem={handleCheckItem(index, true)}
                      handleToggle={handleToggle(index, true)}
                      index={index + newEquipmentItems.length}
                      period={period}
                      checked={item.checked}
                    />
                  ) : (
                    <RowProductDisabled key={index} item={item} index={index} />
                  )
                )}
            </ul>
          )}
        </>
      )}

      <Divider aria-hidden='true' sx={{ m: '40px 0' }} />
      <Typography
        component={'label'}
        htmlFor={'order-note'}
        tabIndex={0}
        sx={{ fontSize: '18px', fontWeight: 'bold', mb: '8px' }}
      >
        {dictionary.orderNote}
      </Typography>
      <TextField
        name='order-note'
        id='order-note'
        placeholder={dictionary.orderNotePlaceholder}
        value={originalOrderNote || undefined}
        multiline
        rows={3}
        sx={{
          backgroundColor: '#fff',
          borderRadius: '6px',

          '.MuiInputBase-formControl': {
            borderRadius: '6px',
            backgroundColor: '#fff',
          },
        }}
        error={error}
        onInput={(e: any) => {
          const newValue = e.target.value.replace(illegalChars, '');
          e.target.value =
            newValue.length > MAX_LENGTH
              ? newValue.substring(0, MAX_LENGTH)
              : newValue;
          setTimeout(() => {
            setOrderNoteCount(e.target.value.length);
          }, 1000);
        }}
        helperText={error ? lettersOrNumbersWithSomeExtraCharsMessage : ''}
        SelectProps={{ sx: { borderRadius: '6px' } }}
      />
      <Typography
        sx={{
          fontSize: '16px !important',
          position: 'relative',
          top: '-30px',
          ml: 'auto',
          p: '0 15px',
          color: 'purple2.main',
        }}
        variant='body7'
        aria-live='assertive'
      >
        {orderNoteCount}/{MAX_LENGTH}
      </Typography>
      {isDesktop ? (
        <>
          <Button
            variant='contained'
            sx={{
              width: isDesktop ? '234px' : '100%',
              alignSelf: 'flex-end',
              fontSize: '18px',
              fontWeight: '600',
              zIndex: 9,
              mb: isDesktop ? undefined : 10,
              position: !isDesktop && scrolled ? 'fixed' : undefined,
              bottom: !isDesktop && scrolled ? 0 : undefined,
              maxWidth: !isDesktop && scrolled ? '88%' : undefined,
              transition: 'transform 0.5s',
              transform: scrolled ? 'translateY(100%)' : 'translateY(0)',
            }}
            type='submit'
            disabled={error}
            endIcon={
              <Avatar
                src={LeftArrowWhite}
                alt=''
                sx={{ width: '20px', height: '20px' }}
              />
            }
          >
            {dictionary.continue}
          </Button>
          {getItemsToDisplay().filter((item) => item.checked).length > 0 && (
            <Typography variant='h5' sx={{ mt: -5 }}>
              {dictionary.chooseXItems(
                getItemsToDisplay().filter((item) => item.checked).length
              )}
            </Typography>
          )}
        </>
      ) : (
        !isSuccessDialogOpen && (
          <Stack
            sx={{
              backgroundColor: '#fff',
              mb:
                getItemsToDisplay().filter((item) => item.checked).length > 0
                  ? '115px'
                  : errorSelectItem
                  ? '135px'
                  : 10,
              position: scrolled ? 'fixed' : undefined,
              transition: 'transform 0.5s',
              transform: scrolled ? 'translateY(100%)' : 'translateY(0)',
              bottom: scrolled ? 0 : undefined,
              zIndex: 9999,
              gap: '10px',
              padding: '20px 0',
              alignItems: 'center',
              ml: -3,
            }}
          >
            {errorSelectItem && (
              <Typography sx={{ color: '#ec0000', mt: 2 }}>
                {dictionary.errorSelectItem}
              </Typography>
            )}
            {getItemsToDisplay().filter((item) => item.checked).length > 0 && (
              <Typography variant='h5'>
                {dictionary.chooseXItems(
                  getItemsToDisplay().filter((item) => item.checked).length
                )}
              </Typography>
            )}
            <Button
              variant='contained'
              sx={{
                width: '100%',
                alignSelf: 'center',
                fontSize: '18px',
                fontWeight: '600',

                maxWidth: scrolled ? '200px' : undefined,
              }}
              type='submit'
              disabled={error}
              endIcon={
                <Avatar
                  src={LeftArrowWhite}
                  alt=''
                  sx={{ width: '20px', height: '20px' }}
                />
              }
            >
              {dictionary.continue}
            </Button>
          </Stack>
        )
      )}

      {isDialogOpen && (
        <SummaryOrderDialog
          open={isDialogOpen}
          handleClose={() => setIsDialogOpen(false)}
          summaryData={orderSummaryData}
          isSuccessDialogOpen={() => setIsSuccessDialogOpen(true)}
          orderPeriod={getPeriodLabel(period)}
        />
      )}
      {isSuccessDialogOpen && (
        <SuccessOrderDialog
          open={isSuccessDialogOpen}
          handleClose={() => setIsSuccessDialogOpen(false)}
          numberOfItems={orderSummaryData.orderItems.length}
        />
      )}
      {isExistingOrderDialogOpen && (
        <ExistingOrderDialog
          date={format(new Date(recentOrders[0].createDate), 'dd.MM.yy')}
          open={isExistingOrderDialogOpen}
          handleClose={() => setIsExistingOrderDialogOpen(false)}
        />
      )}
    </form>
  );
};
